/**
 * HR data-check reports (Emergent_HR_29Sep2026 Part A4 + B4) — read-only.
 *  - PfShortfallReport: PF shortfall by employee, from stored payslips.
 *  - DeptMismatchReport: logins whose Roles & Access department differs from the employee record.
 */
import React, { useState, useEffect } from 'react';
import { Download, Info } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function downloadXlsx(path, params, filename) {
  const p = new URLSearchParams(params);
  const tok = localStorage.getItem('access_token');
  fetch(`${API}/api${path}?${p}`, { headers: { Authorization: `Bearer ${tok}` } })
    .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
    .then(blob => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    })
    .catch(() => toast.error('Export failed'));
}

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const Th = ({ children, right }) => <th className={`px-3 py-2 text-xs font-semibold text-slate-600 ${right ? 'text-right' : 'text-left'}`}>{children}</th>;

export function PfShortfallReport() {
  const [from, setFrom] = useState('2026-04');
  const [to, setTo] = useState('2026-09');
  const [onlyShortfall, setOnlyShortfall] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await apiGet('/hr/reports/pf-shortfall', { from_month: from, to_month: to, only_shortfall: onlyShortfall }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load PF shortfall report'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="pf-shortfall-report">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">From month</label>
          <input type="month" value={from} onChange={e => setFrom(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="pf-shortfall-from" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">To month</label>
          <input type="month" value={to} onChange={e => setTo(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="pf-shortfall-to" />
        </div>
        <label className="flex items-center gap-2 text-sm pb-1.5">
          <input type="checkbox" checked={onlyShortfall} onChange={e => setOnlyShortfall(e.target.checked)} className="rounded" data-testid="pf-shortfall-only" />
          Only rows with a difference
        </label>
        <button onClick={load} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded" data-testid="pf-shortfall-apply">Apply</button>
        <button onClick={() => downloadXlsx('/hr/reports/pf-shortfall/export', { from_month: from, to_month: to, only_shortfall: onlyShortfall }, `pf_shortfall_${from}_${to}.xlsx`)}
          className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] text-sm rounded flex items-center gap-1" data-testid="pf-shortfall-export">
          <Download size={13} /> Export xlsx
        </button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Payslip rows" value={data.row_count} testId="pf-shortfall-rows" />
            <Stat label="Employees affected" value={data.employees_affected} testId="pf-shortfall-employees" />
            <Stat label="Employee share difference" value={inr(data.total_ee_diff)} testId="pf-shortfall-ee-total" />
            <Stat label="Employer share difference" value={inr(data.total_er_diff)} testId="pf-shortfall-er-total" />
          </div>
          {data.by_month?.length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs">
              {data.by_month.map(m => (
                <span key={m.month} className="px-2 py-1 bg-slate-100 rounded-sm font-mono" data-testid={`pf-shortfall-month-${m.month}`}>
                  {m.month}: {m.members} members · EE {inr(m.ee_diff)} · ER {inr(m.er_diff)}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-start gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-sm p-2">
            <Info size={14} className="shrink-0 mt-0.5 text-amber-700" />
            <span>{data.note} Ellora decides on any recovery from this list; nothing here changes a payslip.</span>
          </div>
        </>
      )}
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <Th>Month</Th><Th>Code</Th><Th>Name</Th><Th>UAN</Th>
              <Th right>Basic</Th><Th right>DA</Th>
              <Th right>PF wages used</Th><Th right>PF wages (rule)</Th>
              <Th right>EE deducted</Th><Th right>EE (rule)</Th><Th right>EE diff</Th>
              <Th right>ER paid</Th><Th right>ER (rule)</Th><Th right>ER diff</Th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={14} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : (data?.rows || []).length === 0
                ? <tr><td colSpan={14} className="text-center py-8 text-slate-400">No differences between the stored payslips and the Basic + DA rule for this period.</td></tr>
                : data.rows.map((r, i) => (
                  <tr key={r.payslip_id || i} className="hover:bg-slate-50" data-testid={`pf-shortfall-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.month}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.emp_id}</td>
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.uan || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.basic)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.da)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.pf_wages_used)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.pf_wages_rule)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.ee_used)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.ee_rule)}</td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${r.ee_diff > 0 ? 'text-rose-700' : r.ee_diff < 0 ? 'text-emerald-700' : ''}`}>{inr(r.ee_diff)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.er_used)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.er_rule)}</td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${r.er_diff > 0 ? 'text-rose-700' : r.er_diff < 0 ? 'text-emerald-700' : ''}`}>{inr(r.er_diff)}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DeptMismatchReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await apiGet('/hr/reports/login-employee-dept-mismatch')); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="dept-mismatch-report">
      <div className="flex flex-wrap gap-3 items-center">
        <button onClick={load} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded" data-testid="dept-mismatch-refresh">Refresh</button>
        <button onClick={() => downloadXlsx('/hr/reports/login-employee-dept-mismatch/export', {}, 'login_vs_employee_department.xlsx')}
          className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] text-sm rounded flex items-center gap-1" data-testid="dept-mismatch-export">
          <Download size={13} /> Export xlsx
        </button>
        {data && (
          <span className="text-xs text-slate-500 ml-auto" data-testid="dept-mismatch-count">
            {data.mismatch_count} of {data.total_logins} active logins differ from their employee record
          </span>
        )}
      </div>
      {data && (
        <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2">
          <Info size={14} className="shrink-0 mt-0.5 text-[#3b4872]" />
          <span>{data.note}</span>
        </div>
      )}
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <Th>Name</Th><Th>Login e-mail</Th><Th>Role</Th><Th>Login department (Roles &amp; Access)</Th>
              <Th>Emp code</Th><Th>Employee-record department</Th><Th>Employee status</Th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : (data?.rows || []).length === 0
                ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">Every login's department matches its employee record.</td></tr>
                : data.rows.map((r, i) => (
                  <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`dept-mismatch-row-${i}`}>
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.email}</td>
                    <td className="px-3 py-2 text-xs">{r.role}</td>
                    <td className="px-3 py-2 font-semibold text-[#3b4872]">{r.login_department}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.emp_id}</td>
                    <td className="px-3 py-2 text-amber-800">{r.employee_department}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.employee_status || '—'}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid={testId}>
      <div className="text-xs text-slate-500 truncate">{label}</div>
      <div className="text-2xl font-bold text-[#3b4872]">{value}</div>
    </div>
  );
}


// HR_Payroll_Changes Round 3 #3 — Payroll Gross Mismatch
export function PayrollGrossMismatchReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await apiGet('/hr/reports/payroll-gross-mismatch')); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="payroll-gross-mismatch-report">
      <div className="flex flex-wrap gap-3 items-center">
        <button onClick={load} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded" data-testid="gross-mismatch-refresh">Refresh</button>
        {data && (
          <span className="text-xs text-slate-500 ml-auto" data-testid="gross-mismatch-count">
            {data.total} employee(s) with a gross discrepancy
          </span>
        )}
      </div>
      {data && (
        <div className="flex items-start gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-sm p-2">
          <Info size={14} className="shrink-0 mt-0.5 text-amber-700" />
          <span>{data.note}</span>
        </div>
      )}
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <Th>Code</Th><Th>Name</Th><Th>Department</Th>
              <Th right>Master Gross</Th><Th right>Mirror Sum</Th><Th right>Mirror Diff</Th>
              <Th right>Last Payroll Gross</Th><Th>Month</Th><Th right>Payroll Diff</Th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : (data?.rows || []).length === 0
                ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">All employee gross figures are consistent.</td></tr>
                : data.rows.map((r, i) => (
                  <tr key={r.employee_id} className="hover:bg-slate-50" data-testid={`gross-mismatch-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.emp_id || '—'}</td>
                    <td className="px-3 py-2 font-medium">
                      <a href={`/app/hr/employee-master?id=${r.employee_id}`} className="text-[#3b4872] hover:underline">{r.name}</a>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.department || '—'}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.no_gross_salary ? 'text-rose-600 font-semibold' : ''}`}>{r.no_gross_salary ? '— not set' : inr(r.master_gross)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.mirror_sum)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.mirror_diff !== null && Math.abs(r.mirror_diff) > 0.5 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{r.mirror_diff !== null ? inr(r.mirror_diff) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.last_payroll_gross ? inr(r.last_payroll_gross) : '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.last_payroll_month}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.payroll_diff !== null && Math.abs(r.payroll_diff) > 0.5 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{r.payroll_diff !== null ? inr(r.payroll_diff) : '—'}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// HR_Payroll_Changes Round 3 #3 — PF Opt-Out Compliance
export function PfOptOutComplianceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await apiGet('/hr/reports/pf-optout-compliance')); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="pf-optout-compliance-report">
      <div className="flex flex-wrap gap-3 items-center">
        <button onClick={load} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded" data-testid="pf-optout-refresh">Refresh</button>
        {data && (
          <span className="text-xs text-slate-500 ml-auto" data-testid="pf-optout-count">
            {data.total} employee(s) opted out while PF is mandatory (ceiling ₹{Number(data.pf_ceiling || 25000).toLocaleString('en-IN')})
          </span>
        )}
      </div>
      {data && (
        <div className="flex items-start gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-sm p-2">
          <Info size={14} className="shrink-0 mt-0.5 text-amber-700" />
          <span>{data.note}</span>
        </div>
      )}
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <Th>Code</Th><Th>Name</Th><Th>Department</Th>
              <Th right>Gross</Th><Th right>PF Wages (Basic+DA)</Th><Th right>Ceiling</Th><Th>Issue</Th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : (data?.rows || []).length === 0
                ? <tr><td colSpan={7} className="text-center py-8 text-slate-400">No PF opt-out compliance issues found.</td></tr>
                : data.rows.map((r, i) => (
                  <tr key={r.employee_id} className="hover:bg-slate-50" data-testid={`pf-optout-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.emp_id || '—'}</td>
                    <td className="px-3 py-2 font-medium">
                      <a href={`/app/hr/employee-master?id=${r.employee_id}`} className="text-[#3b4872] hover:underline">{r.name}</a>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.department || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.gross_salary)}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-700 font-semibold">{inr(r.pf_wages)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(r.pf_ceiling)}</td>
                    <td className="px-3 py-2 text-xs text-rose-700">{r.issue}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
